import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Star, CheckCircle2, XCircle, MessageSquare, ThumbsUp, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";

interface ProductReviewsProps {
  productType: string;
  productId: string;
  productName: string;
}

const StarRating = ({ rating }: { rating: number }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((s) => (
      <Star
        key={s}
        className={cn(
          "h-4 w-4",
          s <= rating ? "text-yellow-500 fill-yellow-500" : "text-muted-foreground/30"
        )}
      />
    ))}
  </div>
);

const ProductReviews = ({ productType, productId, productName }: ProductReviewsProps) => {
  const { data: summary } = useQuery({
    queryKey: ["review-summary", productType, productId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("product_review_summaries")
        .select("*")
        .eq("product_type", productType)
        .eq("product_id", productId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const { data: reviews = [] } = useQuery({
    queryKey: ["product-reviews", productType, productId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("product_reviews")
        .select("*")
        .eq("product_type", productType)
        .eq("product_id", productId)
        .eq("status", "published")
        .order("review_date", { ascending: false, nullsFirst: false })
        .limit(20);
      if (error) throw error;
      return data;
    },
  });

  if (!summary && reviews.length === 0) return null;

  return (
    <section className="mb-10">
      <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
        <MessageSquare className="h-5 w-5" /> Customer Reviews
      </h2>

      {/* Summary Card */}
      {summary && (
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Rating Overview */}
              <div className="flex flex-col items-center justify-center md:border-r md:pr-6 min-w-[140px]">
                {summary.average_rating && (
                  <>
                    <span className="text-4xl font-bold text-foreground">{Number(summary.average_rating).toFixed(1)}</span>
                    <StarRating rating={Math.round(Number(summary.average_rating))} />
                    <span className="text-sm text-muted-foreground mt-1">
                      {summary.total_reviews} review{summary.total_reviews !== 1 ? "s" : ""}
                    </span>
                  </>
                )}
              </div>

              {/* AI Summary + Common Pros/Cons */}
              <div className="flex-1">
                {summary.ai_summary && (
                  <p className="text-muted-foreground mb-4 italic">"{summary.ai_summary}"</p>
                )}
                <div className="grid md:grid-cols-2 gap-4">
                  {(summary.common_pros as string[])?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1">
                        <ThumbsUp className="h-3.5 w-3.5 text-primary" /> Most Praised
                      </h4>
                      <ul className="space-y-1">
                        {(summary.common_pros as string[]).slice(0, 4).map((pro, i) => (
                          <li key={i} className="text-sm text-muted-foreground flex items-start gap-1.5">
                            <CheckCircle2 className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                            {pro}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {(summary.common_cons as string[])?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1">
                        <XCircle className="h-3.5 w-3.5 text-destructive" /> Common Criticisms
                      </h4>
                      <ul className="space-y-1">
                        {(summary.common_cons as string[]).slice(0, 4).map((con, i) => (
                          <li key={i} className="text-sm text-muted-foreground flex items-start gap-1.5">
                            <XCircle className="h-3.5 w-3.5 text-destructive mt-0.5 shrink-0" />
                            {con}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Individual Reviews */}
      {reviews.length > 0 && (
        <div className="space-y-4">
          {reviews.map((review: any) => (
            <Card key={review.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      {review.rating && <StarRating rating={review.rating} />}
                      <span className="font-semibold text-sm text-foreground">
                        {review.author_name || "Anonymous"}
                      </span>
                      {review.verified_purchase && (
                        <Badge variant="outline" className="text-[10px] py-0 gap-0.5">
                          <ShieldCheck className="h-3 w-3" /> Verified
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {review.review_date && (
                        <span>{new Date(review.review_date).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" })}</span>
                      )}
                      {review.source && (
                        <Badge variant="secondary" className="text-[10px] py-0 capitalize">{review.source}</Badge>
                      )}
                    </div>
                  </div>
                </div>

                {review.review_text && (
                  <p className="text-sm text-muted-foreground leading-relaxed mb-3">{review.review_text}</p>
                )}

                {((review.pros as string[])?.length > 0 || (review.cons as string[])?.length > 0) && (
                  <div className="flex flex-wrap gap-4 text-xs">
                    {(review.pros as string[])?.length > 0 && (
                      <div className="flex items-start gap-1">
                        <CheckCircle2 className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                        <span className="text-muted-foreground">{(review.pros as string[]).join(", ")}</span>
                      </div>
                    )}
                    {(review.cons as string[])?.length > 0 && (
                      <div className="flex items-start gap-1">
                        <XCircle className="h-3.5 w-3.5 text-destructive mt-0.5 shrink-0" />
                        <span className="text-muted-foreground">{(review.cons as string[]).join(", ")}</span>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
};

export default ProductReviews;
